import { CartUpdateEvent } from '@theme/events';

class CartDrawerRecommendations extends HTMLElement {
  connectedCallback() {
    this.swiperEl = this.querySelector('.cart-drawer-recommendations__swiper');
    this.wrapperEl = this.querySelector('.swiper-wrapper');
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.productId;
    this.limit = Number(this.dataset.limit || 6);
    this.hiddenHandles = (this.dataset.hiddenHandles || '')
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);

    if (!this.swiperEl || !this.wrapperEl) return;

    this.handleClick = this.handleClick.bind(this);
    this.addEventListener('click', this.handleClick);

    this.bootstrap();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.destroySwiper();
  }

  async bootstrap() {
    if (this.wrapperEl.children.length === 0) {
      await this.loadFallbackRecommendations();
    }

    this.initSwiper();
  }

  initSwiper() {
    if (!this.swiperEl || typeof window.Swiper === 'undefined') return;

    this.destroySwiper();

    this.swiper = new window.Swiper(this.swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 10,
      grabCursor: true,
      watchOverflow: false,
      freeMode: {
        enabled: true,
        momentum: true,
      },
      breakpoints: {
        750: {
          slidesPerView: 'auto',
          spaceBetween: 10,
        },
        1200: {
          slidesPerView: 'auto',
          spaceBetween: 10,
        },
      },
    });
  }

  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  async loadFallbackRecommendations() {
    if (!this.productId) return;

    // Over-fetch (API caps at 10) so hidden products don't eat into the
    // visible limit, then filter them out client-side.
    const requestLimit = Math.min(10, this.limit + this.hiddenHandles.length);
    const url = `/recommendations/products.json?product_id=${this.productId}&intent=related&limit=${requestLimit}`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;

    const data = await response.json();
    const products = (Array.isArray(data.products) ? data.products : []).filter(
      (product) => !this.hiddenHandles.includes(product.handle)
    );
    if (products.length === 0) return;

    const cardMarkup = products
      .slice(0, this.limit)
      .map((product) => this.getSlideMarkup(product))
      .join('');

    this.wrapperEl.insertAdjacentHTML('beforeend', cardMarkup);
  }

  /**
   * Resolve the ~30-day (monthly) selling plan + variant for a product from
   * the recommendations JSON so the JS fallback path mirrors the server
   * render: Rx products add as a 1-month subscription. Scans every variant's
   * selling_plan_allocations for a ~30-day plan, falling back to the variant's
   * first plan if no monthly one is found.
   *
   * @param {any} product
   * @returns {{ variant: any, sellingPlanId: number }}
   */
  resolveMonthlyPlan(product) {
    const variants = product.variants || [];
    const daysForPlan = (name) => {
      const lc = String(name || '').toLowerCase();
      const num = Number((lc.match(/\d+/) || [1])[0]) || 1;
      if (lc.includes('month')) return num * 30;
      if (lc.includes('week')) return num * 7;
      if (lc.includes('year')) return num * 365;
      return num;
    };

    // The /recommendations/products.json allocation shape is flat —
    // { price, selling_plan_id, ... } with no nested plan object/name. Build
    // an id -> name map from selling_plan_groups to recover the plan name (and
    // still read alloc.selling_plan.* if a richer shape is ever passed in).
    const planNameById = new Map();
    for (const group of product.selling_plan_groups || []) {
      for (const plan of group.selling_plans || []) {
        if (plan && plan.id != null) planNameById.set(Number(plan.id), plan.name || '');
      }
    }
    const allocPlanId = (alloc) => Number(alloc.selling_plan_id ?? alloc.selling_plan?.id) || 0;
    const allocPlanName = (alloc) => alloc.selling_plan?.name ?? planNameById.get(allocPlanId(alloc)) ?? '';

    for (const v of variants) {
      if (v.available === false) continue;
      const allocations = v.selling_plan_allocations || [];
      for (const alloc of allocations) {
        const days = daysForPlan(allocPlanName(alloc));
        if (days >= 25 && days <= 35) {
          return { variant: v, sellingPlanId: allocPlanId(alloc) };
        }
      }
    }

    const fallbackVariant = variants.find((v) => v.available) || variants[0];
    const firstAlloc = fallbackVariant?.selling_plan_allocations?.[0];
    return { variant: fallbackVariant, sellingPlanId: firstAlloc ? allocPlanId(firstAlloc) : 0 };
  }

  getSlideMarkup(product) {
    const { variant, sellingPlanId } = this.resolveMonthlyPlan(product);
    if (!variant) return '';

    const planAttr = sellingPlanId ? ` data-selling-plan-id="${sellingPlanId}"` : '';
    const rawImage = product.featured_image || '';
    // Append width with the correct separator — a CDN URL may already carry a
    // query string (?v=...) or none at all.
    const imageSrc = rawImage ? `${rawImage}${rawImage.includes('?') ? '&' : '?'}width=420` : '';
    const productUrl = product.url || '#';
    const price = this.formatMoney(variant.price);
    const compare =
      Number(variant.compare_at_price) > Number(variant.price)
        ? `<s class="cart-drawer-recommendations__compare">${this.formatMoney(variant.compare_at_price)}</s>`
        : '';

    return `
      <div class="swiper-slide cart-drawer-recommendations__slide">
        <article class="cart-drawer-recommendations__card">
          <a href="${productUrl}" class="cart-drawer-recommendations__image-link" aria-label="${this.escapeHtml(product.title)}">
            ${imageSrc ? `<img src="${imageSrc}" loading="lazy" class="cart-drawer-recommendations__image" alt="${this.escapeHtml(product.title)}">` : ''}
          </a>
          <div class="cart-drawer-recommendations__meta">
            <a href="${productUrl}" class="cart-drawer-recommendations__name">${this.escapeHtml(product.title)}</a>
            <div class="cart-drawer-recommendations__price-row">
              <span class="cart-drawer-recommendations__price">${price}</span>
              ${compare}
              <button type="button" class="button cart-drawer-recommendations__add" data-recommendation-add data-variant-id="${variant.id}"${planAttr} aria-label="Add ${this.escapeHtml(product.title)} to cart">ADD</button>
            </div>
          </div>
        </article>
      </div>
    `;
  }

  /**
   * Run a cart mutation serialized against every other cart handler, falling
   * back to immediate execution if the shared lock has not loaded.
   * @template T
   * @param {() => Promise<T>} task
   * @returns {Promise<T>}
   */
  runLocked(task) {
    return window.AdegenCartLock ? window.AdegenCartLock.run(task) : task();
  }

  async handleClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[data-recommendation-add]');
    if (!button) return;

    event.preventDefault();
    if (button.hasAttribute('disabled')) return;

    const variantId = Number(button.getAttribute('data-variant-id'));
    if (!variantId) return;

    // Rx products must enter the cart as a 1-month (30-day) subscription, never
    // a one-time purchase. The 30-day selling plan id is rendered server-side
    // onto the button (data-selling-plan-id); attach it to the add payload.
    const sellingPlanId = Number(button.getAttribute('data-selling-plan-id')) || 0;
    const lineItem = { id: variantId, quantity: 1 };
    if (sellingPlanId) lineItem.selling_plan = sellingPlanId;

    button.setAttribute('disabled', 'disabled');
    button.setAttribute('aria-busy', 'true');
    const originalLabel = button.textContent;
    button.textContent = '...';

    try {
      await this.runLocked(async () => {
        // Abort a hung add after 10s so the lock is never held forever and the
        // button doesn't stay stuck on '...' (no timeout previously).
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);
        let response;
        try {
          response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: [lineItem],
              sections: this.sectionId ? [this.sectionId] : [],
              sections_url: window.location.pathname,
            }),
            signal: controller.signal,
          });
        } finally {
          clearTimeout(timeout);
        }

        if (!response.ok) throw new Error('add-failed');

        const data = await response.json();
        let itemCount = data.item_count;
        // /cart/add.js with `sections` returns item_count; only re-fetch the
        // cart in the rare case it's missing.
        if (typeof itemCount !== 'number') {
          const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            itemCount = cart.item_count;
          }
        }

        document.dispatchEvent(
          new CartUpdateEvent(data, 'cart-drawer-recommendations', {
            source: 'cart-drawer-recommendations',
            itemCount: itemCount || 0,
            sections: data.sections,
          })
        );
      });

      button.textContent = 'Added';
      this.announce('Added to cart');
      setTimeout(() => {
        button.textContent = originalLabel || 'ADD';
      }, 1000);
    } catch (err) {
      // Surface the failure instead of silently resetting the label — a quiet
      // reset is indistinguishable from "the button did nothing" (the BUG 3
      // symptom). Log for staging triage and tell the shopper.
      console.error('[cart-recommendations] add to cart failed', { variantId, sellingPlanId }, err);
      button.textContent = 'Retry';
      button.classList.add('cart-drawer-recommendations__add--error');
      this.announce('Could not add to cart. Please try again.');
      setTimeout(() => {
        button.textContent = originalLabel || 'ADD';
        button.classList.remove('cart-drawer-recommendations__add--error');
      }, 1800);
    } finally {
      button.removeAttribute('disabled');
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * Announce a status message to assistive tech via a polite live region so a
   * screen-reader user hears the add/error result (the visible '...' / 'Added'
   * label swap alone is not announced).
   * @param {string} message
   */
  announce(message) {
    if (!this.liveRegion) {
      this.liveRegion = document.createElement('span');
      this.liveRegion.className = 'visually-hidden';
      this.liveRegion.setAttribute('role', 'status');
      this.liveRegion.setAttribute('aria-live', 'polite');
      this.appendChild(this.liveRegion);
    }
    this.liveRegion.textContent = '';
    requestAnimationFrame(() => {
      if (this.liveRegion) this.liveRegion.textContent = message;
    });
  }

  formatMoney(centsValue) {
    const cents = Number(centsValue || 0);
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(cents / 100);
  }

  escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
